import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { env } from "./env";
import { useLocale } from "@/i18n/useLocale";
import { LOCALES, LOCALE_HTML_LANG, DEFAULT_LOCALE, type Locale } from "@/i18n/locales";
import { resolvePath, urlFor } from "@/routes/manifest";

interface PageMeta {
  title: string;
  description?: string;
  image?: string;
}

export function usePageMeta({ title, description, image }: PageMeta): void {
  const { pathname } = useLocation();
  const locale = useLocale();

  useEffect(() => {
    document.title = title;

    // The document's language. Without it a screen reader pronounces German
    // copy with an English voice — an accessibility defect, not just an SEO one.
    document.documentElement.lang = LOCALE_HTML_LANG[locale];
    setMeta("og:locale", OG_LOCALE[locale], "property");

    if (description) {
      setMeta("description", description);
      setMeta("og:description", description, "property");
      setMeta("twitter:description", description);
    }
    setMeta("og:title", title, "property");
    setMeta("twitter:title", title);

    const url = `${env.publicDomain}${pathname}`;
    setLink("canonical", url);
    setMeta("og:url", url, "property");

    // hreflang, RESOLVED from the route manifest rather than compared against a
    // second hand-written list. `seo.md` §9: every page in a set lists ALL
    // versions including itself, the relation must be reciprocal, and there must
    // be one x-default. Derivation is what makes reciprocity structural — a set
    // built by hand drifts the moment a route is added.
    const resolved = resolvePath(pathname);
    const alternates: Array<[string, string]> = resolved
      ? [
          ...LOCALES.map(
            (l): [string, string] => [l, urlFor(resolved.routeId, l)],
          ),
          ["x-default", urlFor(resolved.routeId, DEFAULT_LOCALE)],
        ]
      : [];
    setAlternates(alternates);

    const imageUrl = resolveImageUrl(image);
    setMeta("og:image", imageUrl, "property");
    setMeta("twitter:image", imageUrl);
  }, [title, description, image, pathname, locale]);
}

function resolveImageUrl(image: string | undefined): string {
  if (!image) return `${env.publicDomain}/og-hero.png`;
  return image.startsWith("http") ? image : `${env.publicDomain}${image}`;
}

// Managed as a set: every render replaces the previous alternates wholesale, so
// a navigation cannot leave another page's hreflang entries behind in the head.
const ALTERNATE_ATTR = "data-hreflang-managed";

function setAlternates(pairs: ReadonlyArray<readonly [string, string]>): void {
  document.head
    .querySelectorAll(`link[${ALTERNATE_ATTR}]`)
    .forEach((el) => el.remove());

  for (const [hrefLang, href] of pairs) {
    const el = document.createElement("link");
    el.rel = "alternate";
    el.hreflang = hrefLang;
    el.href = href;
    el.setAttribute(ALTERNATE_ATTR, "");
    document.head.appendChild(el);
  }
}

const OG_LOCALE: Readonly<Record<Locale, string>> = {
  en: "en_US",
  de: "de_DE",
};

function setMeta(name: string, value: string, attr: "name" | "property" = "name"): void {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.content = value;
}

function setLink(rel: string, href: string): void {
  let el = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.rel = rel;
    document.head.appendChild(el);
  }
  el.href = href;
}
